import React, { useEffect, Suspense } from 'react';
import { useLocation, Routes, Route, Navigate } from "react-router-dom";
import ReactGA from 'react-ga';
import 'bootstrap/dist/css/bootstrap.min.css';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Firebase imports
import { ROLES } from './firebase.marketplace.js';

// Context providers
import { AuthProvider } from './contexts/AuthContext.js';
import { CartProvider } from './context/CartContext.js';

// Core components
import GlobalStyle from './globalStyles.js';
import ScrollToTop from './components/ScrollToTop.js';
import Footer2 from './components/Footer2.js';

// Page components
import Home from './components/pages/Home.js';
import Post from './components/pages/Post.js';
import Privacy from './components/pages/Privacy.js';
import About from './components/pages/About.js';
import Upload from './components/pages/Upload.js';
import Advertise from './components/pages/Advertise.js';
import Song from './components/pages/Song.js';

// Audio and video components
import TVPlayer from './components/audio/Songs/Mp3Converter/Converter2.js';
import Converter from './components/audio/Songs/Mp3Converter/Converter.js';
import OnlineRadioPlayer from './components/audio/Songs/Mp3Converter/OnlineRadioPlayer.js';
import RadioStations from './components/pages/RadioStations.js';
import TVchannels from './components/pages/TVchannels.js';
import RecentlyAddedHitsConverter from './components/audio/Songs/Mp3Converter/RecentAddedHitsConverter.js';
import RecentAddedHitsPage from './components/pages/RecentlyAddedHitsPage.js';

// Marketplace components
import MarketplaceRoutes from './components/marketplace/MarketplaceRoutes.js';
import AdminPanel from './components/marketplace/AdminPanel.js';
import ArtistDashboardNew from './components/marketplace/ArtistDashboardNew.js';
// Auth components
import ProtectedRoute from './components/auth/ProtectedRoute.js';
import AdminDashboard from './components/auth/AdminDashboard.js';
import BecomeArtist from './components/auth/BecomeArtist.js';

// Streaming components
import StreamingEventsRoutes from './components/streaming/StreamingEventsRoutes.js';
// Loading spinner component for lazy-loaded routes
const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    width: '100%',
    backgroundColor: '#030617'
  }}>
    <div style={{
      width: '50px',
      height: '50px',
      border: '5px solid rgba(255, 255, 255, 0.1)',
      borderTopColor: '#4a90e2',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
    <style>{`
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

const ExternalRedirect = ({ url }) => {
  useEffect(() => {
    window.location.href = url;
  }, [url]);
  return null;
};

function App() {
  const location = useLocation();

  useEffect(() => {
    // Initialize Google Analytics
    ReactGA.initialize('G-C8BTBL2H7M');
    const trackPage = (page) => {
      ReactGA.set({ page });
      ReactGA.pageview(page);
    };
    
    trackPage(location.pathname + location.search);
  }, [location]);
  
  // Responsive layout wrapper styles
  const appContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    width: '100%',
    overflow: 'hidden',
    position: 'relative'
  };
  
  const mainContentStyle = {
    flex: 1,
    width: '100%',
    maxWidth: '100vw',
    position: 'relative'
  };

  return (
    <AuthProvider>
      <ScrollToTop />
      <GlobalStyle />
      <CartProvider>
        <div style={appContainerStyle}>
          <main style={mainContentStyle}>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/radio/:id" element={<OnlineRadioPlayer />} />
          <Route path="/tv/:id" element={<TVPlayer />} />
          <Route path="/post/:slug" element={<Post />} />
          <Route path="/channels" element={<TVchannels />} />
          <Route path="/radios" element={<RadioStations />} />
          <Route path="/music/:id" element={<Converter />} />
          <Route path="/musics/:id" element={<RecentlyAddedHitsConverter />} />
          <Route path="/musics" element={<RecentAddedHitsPage />} />
          <Route path="/konzohits" element={<Song />} />
          <Route path="/marketplace/*" element={<MarketplaceRoutes />} />
          <Route path="/streaming-events/*" element={<StreamingEventsRoutes />} />
          <Route path="/become-artist" element={<BecomeArtist />} />
          <Route path="/artist-dashboard" element={
            <ProtectedRoute requiredRole="artist">
              <ArtistDashboardNew />
            </ProtectedRoute>
          } />
          <Route path="/admin-dashboard" element={
            <ProtectedRoute requiredRole={ROLES.ADMIN}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin-panel" element={
            <ProtectedRoute requiredRole={ROLES.ADMIN}>
              <AdminPanel />
            </ProtectedRoute>
          } />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/about" element={<About />} />
          <Route path="/upload" element={<Upload />} />
          <Route path="/advertise" element={<Advertise />} />
          {/* Catch all route for unmatched paths */}
          <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </main>
          <Footer2 />
        </div>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
